import {
  createMemo,
  createSignal,
  For,
  Match,
  Show,
  Switch,
  untrack,
  type ParentComponent,
} from "solid-js";
import { Title } from "@solidjs/meta";
import { CopyButton } from "~/common/copy";
import { Divider } from "~/common/divider";
import { FilterButton } from "~/common/filter_tabs";
import { Input } from "~/common/input";
import { Pill } from "~/common/pill";
import { ProgressBar } from "~/common/progress_bar";
import { Icon } from "~/icon";
import { DownloadStep, SetupStep } from "~/link_setup/steps";
import type { LocationState, RemoteSupport } from "~/link_setup/types";
import { accountLinkSetupDownloadPath } from "~/routes";
import { formatDate } from "~/util/format";

type SetupLocation = {
  // go-lives converting from one implementation share its Twelve Link, so one row can name several
  names: string[];
  address: string | null;
  state: LocationState;
  // null on a waiting row when Link Server wouldn't renew its lapsed code
  code: string | null;
  hostname: string | null;
  remoteSupport: RemoteSupport | null;
};

type Props =
  | { active: false }
  | {
      active: true;
      token: string;
      accountName: string;
      expiresAt: string;
      installerAvailable: boolean;
      // null while Link Server can't be read
      locations: SetupLocation[] | null;
    };

const TABS: { state: LocationState; label: string }[] = [
  { state: "waiting", label: "To set up" },
  { state: "connected", label: "Connected" },
  { state: "not_ready", label: "Not ready" },
];

const PublicPageLayout: ParentComponent = (props) => <>{props.children}</>;

function AccountLinkSetup(props: Props) {
  return (
    <>
      <Title>Set up Twelve Link</Title>
      <div class="mx-auto flex min-h-screen w-full max-w-xl flex-col gap-6 p-6 sm:p-12">
        <Show
          when={props.active && props}
          fallback={
            <header class="text-center">
              <h1 class="text-2xl font-semibold text-typography-primary">Set up Twelve Link</h1>
              <p class="mt-2 text-typography-secondary">
                This setup link is no longer active. Ask your Twelve contact for a new one.
              </p>
            </header>
          }
        >
          {(page) => <SetupSteps {...page()} />}
        </Show>
      </div>
    </>
  );
}

function SetupSteps(props: Extract<Props, { active: true }>) {
  const locations = () => props.locations ?? [];
  const count = (state: LocationState) =>
    locations().filter((location) => location.state === state).length;
  // the first tab with anything in it, so a finished account doesn't open on an empty list
  const [tab, setTab] = createSignal<LocationState>(
    untrack(() => TABS.find((option) => count(option.state) > 0)?.state ?? "waiting"),
  );
  const [query, setQuery] = createSignal("");
  const searching = () => query().trim() !== "";
  // a search looks across every tab: the technician knows the location, not which state it's in
  const shown = createMemo(() => {
    const search = query().trim().toLowerCase();
    return locations().filter((location) =>
      search === ""
        ? location.state === tab()
        : `${location.names.join(" ")} ${location.address ?? ""}`.toLowerCase().includes(search),
    );
  });

  return (
    <>
      <header class="text-center">
        <h1 class="text-2xl font-semibold text-typography-primary">Set up Twelve Link</h1>
        <p class="mt-2 text-typography-secondary">
          For {props.accountName}'s{props.locations ? ` ${props.locations.length}` : ""} locations.
          Install Twelve Link on each location's server, then enter that location's setup code.
        </p>
        <Show when={locations().length > 0}>
          <div class="mx-auto mt-4 flex w-full max-w-xs items-center gap-3">
            <p class="text-sm whitespace-nowrap text-typography-secondary">
              <span class="font-medium text-typography-primary">
                {count("connected")} of {locations().length}
              </span>{" "}
              locations connected
            </p>
            <ProgressBar
              class="flex-1"
              size="sm"
              tone="success"
              filledPercentage={(count("connected") / locations().length) * 100}
            />
          </div>
        </Show>
      </header>

      <ol class="flex flex-col gap-6">
        <DownloadStep
          href={accountLinkSetupDownloadPath(props.token)}
          available={props.installerAvailable}
        >
          Download Twelve Link and run the installer on the computer that connects to the location's
          practice management system. It's the same installer for every location.
        </DownloadStep>

        <SetupStep number={2} title="Enter the location's code">
          <p class="text-sm text-typography-secondary">
            When Twelve Link opens, enter the one-time setup code for the location whose server
            you're on.
          </p>
          <p class="mt-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <Icon name="info" size="sm" class="mt-0.5 shrink-0" />
            <span>
              Once it's paired, reload this page: the location shows as connected, with the name of
              the computer it's on. If that's the wrong location, choose Unpair in Twelve Link and
              contact your Twelve representative, since the code you entered is used up.
            </span>
          </p>

          <Show
            when={props.locations}
            fallback={
              <p class="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                We couldn't load the setup codes. Reload this page in a minute.
              </p>
            }
          >
            <div class="sticky top-0 z-10 -mx-2 mt-4 flex flex-col gap-2 bg-white px-2 py-2">
              <Input
                beforeIcon="search"
                placeholder="Search by location or address"
                width="full"
                clearable
                value={query()}
                onInput={(e) => setQuery(e.currentTarget.value)}
              />
              <Show
                when={!searching()}
                fallback={
                  <p class="text-xs text-typography-secondary">
                    {shown().length} of {locations().length} locations match
                  </p>
                }
              >
                <div class="flex flex-wrap gap-1">
                  <For each={TABS}>
                    {(option) => (
                      <FilterButton
                        label={option.label}
                        count={count(option.state)}
                        active={tab() === option.state}
                        onClick={() => setTab(option.state)}
                      />
                    )}
                  </For>
                </div>
              </Show>
            </div>

            <ul class="flex flex-col">
              <For
                each={shown()}
                fallback={
                  <li class="py-6 text-center text-sm text-typography-secondary">
                    {searching() ? `No location matches "${query().trim()}".` : "Nothing here."}
                  </li>
                }
              >
                {(location, index) => (
                  <li>
                    <Show when={index() > 0}>
                      <Divider />
                    </Show>
                    <div class="flex items-center justify-between gap-3 py-3">
                      <div class="min-w-0">
                        <p
                          class={`font-medium ${location.state === "not_ready" ? "text-typography-secondary" : "text-typography-primary"}`}
                        >
                          {location.names.join(", ")}
                        </p>
                        <Show
                          when={location.state === "connected" && location.hostname}
                          fallback={
                            <Show when={location.address}>
                              {(address) => (
                                <p class="truncate text-xs text-typography-secondary">
                                  {address()}
                                </p>
                              )}
                            </Show>
                          }
                        >
                          {(hostname) => (
                            <p class="truncate text-xs text-typography-secondary">
                              On <span class="font-mono">{hostname()}</span>
                            </p>
                          )}
                        </Show>
                      </div>
                      <Switch>
                        <Match when={location.state === "waiting"}>
                          <Show
                            when={location.code}
                            fallback={
                              <span class="shrink-0 text-right text-xs text-typography-secondary">
                                No code right now. Reload in a minute.
                              </span>
                            }
                          >
                            {(code) => (
                              <div class="flex shrink-0 items-center gap-2">
                                <code class="inline-block rounded-lg bg-gray-100 px-3 py-1 text-lg font-bold tracking-widest text-typography-primary">
                                  {code()}
                                </code>
                                <CopyButton text={code()} label="Copy" />
                              </div>
                            )}
                          </Show>
                        </Match>
                        <Match when={location.state === "connected"}>
                          <Pill
                            variant={location.remoteSupport === "installing" ? "gray" : "success"}
                            size="sm"
                          >
                            {location.remoteSupport === "installing"
                              ? "Connected · installing remote support"
                              : "Connected"}
                          </Pill>
                        </Match>
                        <Match when={location.state === "not_ready"}>
                          <span class="shrink-0 text-xs text-typography-secondary">
                            Code appears here when ready
                          </span>
                        </Match>
                      </Switch>
                    </div>
                  </li>
                )}
              </For>
            </ul>
          </Show>

          <p class="mt-2 text-xs text-typography-secondary">
            Codes expire after 3 days, and this page shows a new one when a code expires. This link
            works until {formatDate(props.expiresAt)}.
          </p>
        </SetupStep>
      </ol>
    </>
  );
}

AccountLinkSetup.layout = PublicPageLayout;
export default AccountLinkSetup;
